import { Redirect, Route, Switch } from 'react-router-dom';
import ContactHome from '../views/ContactHome';
import Mtls from '../views/Mtls';
import { Home } from '../views/Home';
import { Agent } from '../views/Agent';
import { billingRoutes, BillingRoutes } from './BillingRoutes';
import { paths } from './paths';
import { BillingMetrics } from '../shared/BillingMetrics';
import {
  accessPermission,
  canUserRead,
  canUserWrite,
  canAccessKnowledgeBase,
  canAccessPipeline,
} from '../services/PermissionService';
import { useTenant } from '../contexts/TenantContext';
import { PrivateRoute, PrivateRouteAsync } from '../components/PrivateRoute';
import {
  CONTRACT_MEMBERS,
  CONTRACT_REVENUE_CLICK,
  DAILY_ACTIVE_USERS_CLICK,
  DAILY_ENGAGED_USERS_CLICK_CLICK,
  ORGANIZATION_AGENTS,
  SESSION_CONSUMPTION_CLICK,
  AGENT_CONSUMPTION_CLICK,
  REPORT_OVERVIEW_CONSUMPTION_OPEN,
  ORGANIZATION_CALLS_MENU,
  ORGANIZATION_MTLS,
  ORGANIZATION_KNOWLEDGE_BASE,
  ORGANIZATION_PIPELINE,
  ORGANIZATION_ACCESS_GROUPS,
} from '../shared/SegmentEvents';
import {
  DAILY_ACTIVE_USERS_CONSUMPTION,
  DAILY_ENGAGED_USERS_CONSUMPTION,
  SESSION_CONSUMPTION,
  AGENT_CONSUMPTION,
  DATA_CONSUMPTION,
  TENANT_CONSUMPTION,
  MONTHLY_ENGAGED_USERS_CONSUMPTION,
  CALLS_SETTINGS,
  ENABLE_MTLS,
  PIPELINE_SCREENS_ENABLED,
  ACCESS_GROUPS,
} from '../shared/FeatureToggleKeys';
import Copilot from '../views/Copilot';
import { RouteOptions } from '../models/shared/RouteOptions';
import Calls from '../views/Calls';
import { useCommon } from '../contexts/CommonContext';
import KnowledgeBase from '../views/KnowledgeBase';
import Documents from '../views/KnowledgeBase/Documents';
import UrlPaths from '../views/KnowledgeBase/Documents/UrlPaths';
import Pipeline from '../views/pipeline';

export const generalSettingsOptions: RouteOptions[] = [
  {
    group: 'generalSettings',
    option: 'members',
    icon: 'avatar-user',
    accessPermission: accessPermission.members,
    segmentEvent: CONTRACT_MEMBERS,
    path: paths.members,
    additionalCheck: canUserRead,
  },
  {
    group: 'generalSettings',
    option: 'agent',
    icon: 'agent',
    accessPermission: accessPermission.billing,
    segmentEvent: ORGANIZATION_AGENTS,
    metrics: BillingMetrics.agent,
    path: paths.agent,
    additionalCheck: canUserRead,
  },
  {
    group: 'generalSettings',
    option: 'mtls',
    icon: 'lock',
    featureToggle: ENABLE_MTLS,
    accessPermission: accessPermission.members,
    segmentEvent: ORGANIZATION_MTLS,
    path: paths.mtls,
    additionalCheck: canUserRead,
  },
  {
    group: 'generalSettings',
    option: 'knowledgeBase',
    icon: 'brain-ai',
    accessPermission: accessPermission.workspace,
    segmentEvent: ORGANIZATION_KNOWLEDGE_BASE,
    path: paths.knowledgeBase,
    additionalCheck: canAccessKnowledgeBase,
  },
  {
    group: 'generalSettings',
    option: 'pipeline',
    icon: 'builder-router',
    featureToggle: PIPELINE_SCREENS_ENABLED,
    accessPermission: accessPermission.members,
    segmentEvent: ORGANIZATION_PIPELINE,
    path: paths.pipeline,
    additionalCheck: canAccessPipeline,
  },
  {
    group: 'generalSettings',
    option: 'accessGroups',
    icon: 'team',
    featureToggle: ACCESS_GROUPS,
    accessPermission: accessPermission.permissionsGroup,
    segmentEvent: ORGANIZATION_ACCESS_GROUPS,
    path: paths.accessGroups,
    additionalCheck: canUserRead,
  },
];

export const featureManagementOptions: RouteOptions[] = [
  {
    group: 'featureManagement',
    option: 'blipCalls',
    icon: 'robot',
    featureToggle: CALLS_SETTINGS,
    accessPermission: accessPermission.members,
    segmentEvent: ORGANIZATION_CALLS_MENU,
    path: paths.calls,
    additionalCheck: canUserWrite,
  },
];

export const usageDataOptions: RouteOptions[] = [
  {
    group: 'usageData',
    option: 'contractRevenue',
    icon: 'file-txt-1',
    featureToggle: [{key: TENANT_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: CONTRACT_REVENUE_CLICK,
    metrics: [BillingMetrics.monthlyActiveUser, BillingMetrics.exchangedMessage],
    path: billingRoutes.revenue,
  },
  {
    group: 'usageData',
    option: 'contractRevenue',
    icon: 'file-txt-1',
    featureToggle: [{key: MONTHLY_ENGAGED_USERS_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: CONTRACT_REVENUE_CLICK,
    metrics: [BillingMetrics.monthlyEngagedUser, BillingMetrics.exchangedMessage],
    path: billingRoutes.revenue,
  },
  {
    group: 'usageData',
    option: 'dailyActiveUsers',
    icon: 'user-engaged',
    featureToggle: [{key: DAILY_ACTIVE_USERS_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: DAILY_ACTIVE_USERS_CLICK,
    metrics: [BillingMetrics.dailyActiveUser],
    path: billingRoutes.dailyActiveUsers,
  },
  {
    group: 'usageData',
    option: 'dailyEngagedUsers',
    icon: 'user-engaged',
    featureToggle: [{key: DAILY_ENGAGED_USERS_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: DAILY_ENGAGED_USERS_CLICK_CLICK,
    metrics: [BillingMetrics.dailyEngagedUser],
    path: billingRoutes.dailyEngagedUsers,
  },
  {
    group: 'usageData',
    option: 'sessionConsumption',
    icon: 'message-talk',
    featureToggle: [{key: SESSION_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: SESSION_CONSUMPTION_CLICK,
    metrics: [BillingMetrics.session],
    path: billingRoutes.oldSessionConsumption,
  },
  {
    group: 'usageData',
    option: 'agentConsumption',
    icon: 'agent',
    featureToggle: [{key: AGENT_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: AGENT_CONSUMPTION_CLICK,
    metrics: [BillingMetrics.agent],
    path: billingRoutes.oldAgentConsumption,
  },
  {
    group: 'usageData',
    option: 'dataConsumption',
    icon: 'monitoring',
    featureToggle: [{key: DATA_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_OVERVIEW_CONSUMPTION_OPEN,
    metrics: Object.values(BillingMetrics).map<BillingMetrics>((metric) => metric as BillingMetrics),
    path: billingRoutes.dataConsumption,
  },
];

const Routes = (): JSX.Element => {
  const { tenant } = useTenant();
  const {
    common: { loggedUser },
  } = useCommon();

  const hasEnabledMetric = (metricId: BillingMetrics): boolean =>
    tenant.subscription?.some((item: { metricId: BillingMetrics }) => item.metricId === metricId) || false;

  return (
    <Switch>
      <Route path={paths.home} exact component={ContactHome} />
      <Route path={paths.members} exact component={Home} />
      <Route path={paths.copilot} exact component={Copilot} />
      <Route path={paths.calls} exact component={Calls} />
      <PrivateRouteAsync
        path={paths.knowledgeBase}
        exact
        component={KnowledgeBase}
        hasPermission={() => canAccessKnowledgeBase(accessPermission.workspace, loggedUser.roleId, tenant.info.id)}
      />
      <PrivateRouteAsync
        path={paths.knowledgeBaseDocuments}
        exact
        component={Documents}
        hasPermission={() => canAccessKnowledgeBase(accessPermission.workspace, loggedUser.roleId, tenant.info.id)}
      />
      <PrivateRouteAsync
        path={paths.knowledgeBaseUrlPaths}
        exact
        component={UrlPaths}
        hasPermission={() => canAccessKnowledgeBase(accessPermission.workspace, loggedUser.roleId, tenant.info.id)}
      />
      <PrivateRouteAsync
        path={paths.pipeline}
        exact
        component={Pipeline}
        hasPermission={() => canAccessPipeline(accessPermission.members, loggedUser.roleId, tenant.info.id)}
      />
      <PrivateRoute
        path={paths.mtls}
        exact
        component={Mtls}
        hasPermission={() => canUserRead(accessPermission.members, loggedUser.roleId)}
      />
      <PrivateRoute
        path={paths.agent}
        exact
        component={Agent}
        hasPermission={() => hasEnabledMetric(BillingMetrics.agent)}
      />
      <BillingRoutes />
      <Redirect to={paths.home} />
    </Switch>
  );
};

export { Routes };
